/**
 * REST dataProvider (NestJS API).
 */
import { getPanelAccessToken } from './apiAuthProvider';
import { transformEventDataForForm } from '../utils/eventFormUtils';
import { expandMediaUrlsDeep, stripMediaUrlsDeep } from '../utils/mediaUrl';
import { withEventSlugAsRaId } from '../utils/eventIdentity';
import { sanitizeHeroRecord, sanitizeHeroPayload } from '../utils/heroRecordUtils';

const getApiUrl = () => {
  const base = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';
  return base.replace(/\/$/, '');
};

// react-admin resource adi -> API path
export const API_RESOURCE_PATHS = {
  'events-v2': 'events',
  stocks: 'stocks',
  groupEvents: 'group-events',
  users: 'users',
  // Her koleksiyon ayri MySQL tablosu + ayri REST path (documents tablosu yok)
  cities: 'cities',
  categories: 'categories',
  subcategories: 'subcategories',
  venues: 'venues',
  venueCategories: 'venueCategories',
  organisationCompanies: 'organisationCompanies',
  systemParameters: 'systemParameters',
  appVersionConfigs: 'appVersionConfigs',
  layouts: 'layouts',
  salePoints: 'salePoints',
  saleAgents: 'saleAgents',
  promoters: 'promoters',
  affiliates: 'affiliates',
  affiliateForm: 'affiliateForm',
  eventCreationApplications: 'eventCreationApplications',
  adPackages: 'adPackages',
  adApplications: 'adApplications',
  featureds: 'featureds',
  eventTags: 'eventTags',
  deleteUserRequests: 'deleteUserRequests',
  refundRequests: 'refundRequests',
  homeSections: 'homeSections',
  heros: 'heros',
  pages: 'pages',
  news: 'news',
  scripts: 'scripts',
  settlementCategories: 'settlementCategories',
  sessionEvents: 'sessionEvents',
  events: 'event-archives',
  eventLogs: 'eventLogs',
  verificationLogs: 'verificationLogs',
  paymentLogs: 'paymentLogs',
  payments: 'payments',
  sales: 'sales',
  tickets: 'tickets',
  'sales-v2': 'sales-v2',
  eventLayouts: 'eventLayouts',
  seatSelections: 'seatSelections',
  eventStats: 'stats',
};

const authHeaders = (extra = {}) => {
  const token = getPanelAccessToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const parseJson = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const request = async (path, options = {}) => {
  const response = await fetch(`${getApiUrl()}/${path}`, options);
  const data = await parseJson(response);
  if (!response.ok) {
    const message =
      (data && (Array.isArray(data.message) ? data.message.join(', ') : data.message)) ||
      `API error (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return data;
};

const pathFor = (resource) => API_RESOURCE_PATHS[resource];

/** Tek bir react-admin image degerini (rawFile varsa) API'ye yukler. */
const uploadOne = async (value, { folder } = {}) => {
  if (!value?.rawFile) {
    // blob: URL asla DB'ye yazilmaz
    if (typeof value?.src === 'string' && value.src.startsWith('blob:')) {
      throw new Error('Görsel yüklenemedi (blob). Lütfen görseli tekrar seçip kaydedin.');
    }
    if (typeof value?.src === 'string' && value.src.startsWith('data:')) {
      throw new Error('Görsel yüklenemedi (data URL). Lütfen görseli tekrar seçip kaydedin.');
    }
    return value;
  }
  const form = new FormData();
  const filename = value.rawFile.name || value.title || 'upload.jpeg';
  const file =
    value.rawFile instanceof File
      ? value.rawFile
      : new File([value.rawFile], filename, {
          type: value.rawFile.type || 'image/jpeg',
        });
  form.append('file', file, filename);
  const qs = folder ? `?folder=${encodeURIComponent(folder)}` : '';
  const uploaded = await request(`upload${qs}`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  if (
    !uploaded?.src ||
    typeof uploaded.src !== 'string' ||
    uploaded.src.startsWith('blob:') ||
    uploaded.src.startsWith('data:') ||
    !uploaded.src.includes('/uploads/')
  ) {
    throw new Error('Upload failed — server did not return a /uploads/ path');
  }
  // Ham dosya / blob onizleme asla payload'da kalmasin
  return { src: uploaded.src, title: uploaded.title || filename };
};

const isPlainObject = (value) =>
  value != null &&
  typeof value === 'object' &&
  !(value instanceof Date) &&
  !(value instanceof Blob) &&
  !(value instanceof File) &&
  !Array.isArray(value);

/**
 * Veri icindeki herhangi bir alanda (nested/array dahil) rawFile (Blob) tasiyan
 * gorselleri otomatik bulur, API'ye yukler ve { src, title } ile degistirir.
 * Boylece her resource icin alan listesi tutmaya gerek kalmaz.
 *
 * Onemli: specialEvents[].featuredImage gibi nested alanlari da gezer —
 * sadece ust seviye / dizi ogesi image objesi sanilirsa blob: URL DB'ye yazilirdi.
 */
const processValue = async (value, options = {}) => {
  if (Array.isArray(value)) {
    return Promise.all(value.map((item) => processValue(item, options)));
  }
  if (!isPlainObject(value)) return value;

  // Image-like: ham dosya veya (hatali) blob src
  if (value.rawFile instanceof Blob) {
    return uploadOne(value, options);
  }
  if (typeof value.src === 'string' && value.src.startsWith('blob:')) {
    return uploadOne(value, options);
  }
  if (typeof value.src === 'string' && value.src.startsWith('data:')) {
    return uploadOne(value, options);
  }

  // Nested kayit (ornegin specialEvents satiri) — alt alanlari tara
  const out = {};
  for (const [key, val] of Object.entries(value)) {
    // rawFile kalintilarini (JSON.stringify File → {path}) at
    if (key === 'rawFile') continue;
    out[key] = await processValue(val, options);
  }
  return out;
};

/** Son guvenlik: payload icinde blob:/data: src kalmasin. */
const assertNoTransientMediaSrc = (value, path = 'root') => {
  if (Array.isArray(value)) {
    value.forEach((item, i) => assertNoTransientMediaSrc(item, `${path}[${i}]`));
    return;
  }
  if (!isPlainObject(value)) return;
  if (typeof value.src === 'string') {
    const src = value.src.trim();
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      throw new Error(`Görsel blob/data olarak kaydedilemez (${path}.src). Tekrar seçip kaydedin.`);
    }
  }
  if (value.rawFile != null) {
    throw new Error(`Ham dosya (rawFile) DB'ye yazılamaz (${path}). Upload tamamlanmamış.`);
  }
  Object.entries(value).forEach(([key, val]) => {
    if (key === 'src') return;
    assertNoTransientMediaSrc(val, `${path}.${key}`);
  });
};

/** Hero / event gorselleri alt klasore. */
const uploadFolderForResource = (resource) => {
  if (resource === 'heros') return 'ozelbanergorsel';
  if (resource === 'events-v2' || resource === 'events') return 'event_images';
  return undefined;
};

const uploadFiles = async (resource, data) => {
  const folder = uploadFolderForResource(resource);
  const options = folder ? { folder } : {};
  const out = { ...data };
  for (const key of Object.keys(out)) {
    out[key] = await processValue(out[key], options);
  }
  // DB'ye her zaman relative /uploads/... yaz
  const stripped = stripMediaUrlsDeep(out);
  assertNoTransientMediaSrc(stripped);
  return stripped;
};

const buildListQuery = (params) => {
  const { page = 1, perPage = 100 } = params.pagination || {};
  const { field, order } = params.sort || {};
  const filter = params.filter || {};

  const query = new URLSearchParams();
  query.set('page', String(page));
  query.set('perPage', String(perPage));
  if (field) query.set('sort', field);
  if (order) query.set('order', String(order).toLowerCase());

  Object.entries(filter).forEach(([key, val]) => {
    if (val === undefined || val === null || val === '') return;
    // react-admin tam metin aramasi 'q' olarak gelir
    query.set(key === 'q' ? 'q' : key, String(val));
  });

  return query.toString();
};

const forPanelDisplay = (data) => expandMediaUrlsDeep(data);

/** @param {string} [requestedId] URL/RA id (slug veya cuid) */
const forEventPanel = (data, requestedId) =>
  withEventSlugAsRaId(forPanelDisplay(data), requestedId);

const apiDataProvider = {
  getList: async (resource, params) => {
    const path = pathFor(resource);
    const qs = buildListQuery(params);
    const res = await request(`${path}?${qs}`, { headers: authHeaders() });
    const rows = (res.data ?? []).map((row) => {
      if (resource === 'events-v2') return forEventPanel(row);
      if (resource === 'heros') {
        return sanitizeHeroRecord(forPanelDisplay(row), row.id);
      }
      return forPanelDisplay(row);
    });
    return { data: rows, total: res.total ?? rows.length };
  },

  getOne: async (resource, params) => {
    const path = pathFor(resource);
    let data = await request(`${path}/${encodeURIComponent(params.id)}`, {
      headers: authHeaders(),
    });
    if (resource === 'events-v2') {
      data = forEventPanel(transformEventDataForForm(data), params.id);
      return { data };
    }
    if (resource === 'heros') {
      return {
        data: sanitizeHeroRecord(forPanelDisplay(data), params.id),
      };
    }
    return { data: forPanelDisplay(data) };
  },

  getMany: async (resource, params) => {
    const path = pathFor(resource);
    const ids = (params.ids || []).map(String).filter(Boolean);
    if (!ids.length) return { data: [] };

    try {
      const qs = new URLSearchParams({
        ids: ids.join(','),
        perPage: String(Math.min(ids.length, 200)),
        page: '1',
      });
      const res = await request(`${path}?${qs.toString()}`, {
        headers: authHeaders(),
      });
      const rows = (res?.data ?? []).map((row) =>
        resource === 'events-v2' ? forEventPanel(row, row.id) : forPanelDisplay(row),
      );
      if (rows.length) return { data: rows };
    } catch (err) {
      // Batch desteklenmiyorsa tek tek dus
      if (err.status && err.status !== 404) {
        // continue to fallback
      }
    }

    const results = await Promise.all(
      ids.map(async (id) => {
        try {
          const row = await request(`${path}/${encodeURIComponent(id)}`, {
            headers: authHeaders(),
          });
          return resource === 'events-v2' ? forEventPanel(row, id) : forPanelDisplay(row);
        } catch (err) {
          if (err.status === 404) return null;
          throw err;
        }
      }),
    );
    return { data: results.filter(Boolean) };
  },

  getManyReference: async (resource, params) => {
    const path = pathFor(resource);
    const query = new URLSearchParams();
    const { page = 1, perPage = 100 } = params.pagination || {};
    query.set('page', String(page));
    query.set('perPage', String(perPage));
    if (params.target) query.set(params.target, String(params.id));
    const res = await request(`${path}?${query.toString()}`, { headers: authHeaders() });
    const rows = (res.data ?? []).map((row) => forPanelDisplay(row));
    return { data: rows, total: res.total ?? 0 };
  },

  create: async (resource, params) => {
    const path = pathFor(resource);
    const raw =
      resource === 'heros'
        ? sanitizeHeroPayload(params.data, params.data?.id ?? 'home')
        : params.data;
    const payload = await uploadFiles(resource, raw);
    if (resource === 'events-v2' && payload && typeof payload === 'object') {
      delete payload.uid;
      delete payload.id;
    }
    const data = await request(path, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
    return {
      data:
        resource === 'events-v2'
          ? forEventPanel(data)
          : resource === 'heros'
            ? sanitizeHeroRecord(forPanelDisplay(data), data?.id ?? 'home')
            : forPanelDisplay(data),
    };
  },

  update: async (resource, params) => {
    const path = pathFor(resource);
    const payload =
      resource === 'heros'
        ? await uploadFiles(resource, sanitizeHeroPayload(params.data, params.id))
        : await uploadFiles(resource, params.data);
    if (resource === 'events-v2' && payload && typeof payload === 'object') {
      // Panel RA id = slug; DB cuid uid'de. Payload'a ikisini de gonderme.
      delete payload.uid;
      delete payload.id;
    }
    // events-v2: URL slug veya cuid olabilir; API ikisini de cozer
    const data = await request(`${path}/${encodeURIComponent(params.id)}`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
    return {
      data:
        resource === 'events-v2'
          ? forEventPanel(data, params.id)
          : resource === 'heros'
            ? sanitizeHeroRecord(forPanelDisplay(data), params.id)
            : forPanelDisplay(data),
    };
  },

  updateMany: async (resource, params) => {
    await Promise.all(
      (params.ids || []).map((id) => apiDataProvider.update(resource, { id, data: params.data })),
    );
    return { data: params.ids };
  },

  delete: async (resource, params) => {
    const path = pathFor(resource);
    const hard = params.meta?.hard === true;
    const query = hard ? '?mode=hard' : '';
    await request(`${path}/${encodeURIComponent(params.id)}${query}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return { data: params.previousData ?? { id: params.id } };
  },

  deleteMany: async (resource, params) => {
    await Promise.all(
      (params.ids || []).map((id) =>
        request(`${pathFor(resource)}/${id}`, {
          method: 'DELETE',
          headers: authHeaders(),
        }),
      ),
    );
    return { data: params.ids };
  },
};

export default apiDataProvider;
